import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';

const ACTIVATE_DELAY = 150;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu with mega menu support.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Setup mobile menu toggle
    this.#setupMobileMenu();

    // Preload images
    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 350;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Setup mobile menu behavior
   */
  #setupMobileMenu() {
    const mobileMenuItems = this.querySelectorAll('.menu-list__link--with-submenu');
    
    mobileMenuItems.forEach(link => {
      // On mobile, prevent default and toggle submenu
      link.addEventListener('click', (e) => {
        if (window.innerWidth < 750) {
          e.preventDefault();
          const listItem = link.closest('.menu-list__list-item');
          const submenu = listItem?.querySelector('.mega-menu__submenu');
          
          if (submenu) {
            const isOpen = submenu.style.maxHeight && submenu.style.maxHeight !== '0px';
            
            // Close all other submenus
            this.querySelectorAll('.mega-menu__submenu').forEach(sub => {
              if (sub !== submenu) {
                sub.style.maxHeight = '0px';
                sub.style.opacity = '0';
              }
            });
            
            // Toggle this submenu
            if (isOpen) {
              submenu.style.maxHeight = '0px';
              submenu.style.opacity = '0';
            } else {
              submenu.style.maxHeight = submenu.scrollHeight + 'px';
              submenu.style.opacity = '1';
            }
          }
        }
      });
    });
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) {
        prevSubmenu.style.opacity = '0';
        prevSubmenu.style.visibility = 'hidden';
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.style.opacity = '1';
      submenu.style.visibility = 'visible';
      
      const submenuHeight = Math.max(submenu.offsetHeight, overflowMenuHeight);
      this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    const submenu = findSubmenu(item);
    
    if (submenu) {
      submenu.style.opacity = '0';
      submenu.style.visibility = 'hidden';
    }

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150));
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => {
      // Only preload images in mega menus, not all images
      if (image.closest('.mega-menu__submenu')) {
        image.removeAttribute('loading');
      }
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}